import React, { useEffect, useMemo, useRef, useState } from 'react';

import { FUNCTION_NAMES } from '../constants';
import { API_BASE_URL } from '../config';
import { fetchWithRetry } from '../utils/tourUtils';
import PublicTourLinkManager from './PublicTourLinkManager';
import AccordionPanel from './ui/accordion-panel';
import LotBuildingSelectorWithSVG from './LotBuildingSelectorWithSVG';
import SpaceModal from './SpaceModal';
import { Settings, Save, ChevronLeft, RotateCcw, Home, FileText, Paperclip, Video, Image, Eye, ChevronDown, ChevronUp, Download } from 'lucide-react';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { useUser } from './UserContext';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

// File viewer modal component
const FileViewerModal = ({ isOpen, onClose, fileUrl, fileName, fileType }) => {
  const renderFileContent = () => {
    if (!fileUrl) return <div>Loading...</div>;

    const lowerFileName = fileName?.toLowerCase() || '';
    
    // Image files
    if (fileType === 'IMAGE360' || fileType === 'TABLETIMAGE' || 
        lowerFileName.match(/\.(jpg|jpeg|png|gif|bmp|webp)$/)) {
      return (
        <img 
          src={fileUrl} 
          alt={fileName}
          className="max-w-full max-h-[70vh] object-contain mx-auto"
          style={{ maxWidth: '100%', height: 'auto' }}
        />
      );
    }
    
    // Video files
    if (fileType === 'VIDEO' || lowerFileName.match(/\.(mp4|webm|ogg|avi|mov)$/)) {
      return (
        <video 
          controls 
          className="max-w-full max-h-[70vh] mx-auto"
          style={{ maxWidth: '100%', height: 'auto' }}
        >
          <source src={fileUrl} />
          Your browser does not support the video tag.
        </video>
      );
    }
    
    // PDF files
    if (lowerFileName.match(/\.pdf$/)) {
      return (
        <iframe
          src={fileUrl}
          className="w-full h-[70vh]"
          title={fileName}
        />
      );
    }
    
    // Other document types - show download option
    return (
      <div className="text-center py-8">
        <Paperclip className="h-16 w-16 mx-auto text-gray-400 mb-4" />
        <p className="text-lg font-medium mb-2">{fileName}</p>
        <p className="text-gray-600 mb-4">This file type cannot be previewed</p>
        <Button onClick={() => window.open(fileUrl, '_blank')}>
          <Download className="h-4 w-4 mr-2" />
          Download File
        </Button>
      </div>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>{fileName}</span>
            <div className="flex gap-2">
              <Button 
                size="sm" 
                variant="outline" 
                onClick={() => window.open(fileUrl, '_blank')}
              >
                <Download className="h-4 w-4 mr-1" />
                Download
              </Button>
            </div>
          </DialogTitle>
        </DialogHeader>
        
        <div className="overflow-auto max-h-[75vh]">
          {renderFileContent()}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Component to display S3 file with view/download options
const S3FileViewer = ({ filePath, s3Path, attachmentType, fileName }) => {
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const [fileUrl, setFileUrl] = useState(null);
  const getFileIcon = (type) => {
    switch (type) {
      case 'IMAGE360':
      case 'TABLETIMAGE':
        return <Image className="h-4 w-4" />;
      case 'VIDEO':
        return <Video className="h-4 w-4" />;
      default:
        return <Paperclip className="h-4 w-4" />;
    }
  };

  const handleView = async () => {
    if (!s3Path) return;
    
    try {
      const response = await fetchWithRetry({
        url: `${API_BASE_URL}/api/post`,
        data: {
          function_name: FUNCTION_NAMES.POST.GET_PRESIGNED_URL,
          payload: { 
            s3Key: s3Path,
            expiresIn: 3600 // 1 hour expiry
          },
        },
        label: 'get presigned URL',
      });
      
      if (response.success && response.url) {
        setFileUrl(response.url);
        setIsViewerOpen(true);
      }
    } catch (error) {
      console.error('Error viewing file:', error);
    }
  };


  return (
    <>
      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
        <div className="flex items-center space-x-3">
          {getFileIcon(attachmentType)}
          <div>
            <p className="text-sm font-medium">{fileName || filePath.split('/').pop()}</p>
            {s3Path && <p className="text-xs text-gray-500">{s3Path}</p>}
          </div>
          <Badge variant={s3Path ? "success" : "secondary"}>
            {s3Path ? "Uploaded" : "Pending"}
          </Badge>
        </div>
        
        {s3Path && (
          <div className="flex space-x-2">
            <Button size="sm" variant="outline" onClick={handleView}>
              <Eye className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>

      <FileViewerModal
        isOpen={isViewerOpen}
        onClose={() => setIsViewerOpen(false)}
        fileUrl={fileUrl}
        fileName={fileName || filePath.split('/').pop()}
        fileType={attachmentType}
      />
    </>
  );
};

const ClientSelectorWithImages = ({ onClientSelect }) => {
  const [clients, setClients] = useState([]);

  useEffect(() => {
    fetchWithRetry({
      url: `${API_BASE_URL}/api/post`,
      data: {
        function_name: FUNCTION_NAMES.PRISMA,
        operation: 'client.findMany',
        payload: {},
      },
      label: 'clients',
    }).then(setClients);
  }, []);

  return (
    <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 md:gap-4">
      {clients.map(client => (
        <div
          key={client.id}
          onClick={() => onClientSelect(client.id)}
          className="cursor-pointer group rounded-xl overflow-hidden border border-border hover:border-primary hover:shadow-md transition-all duration-200"
        >
          <div className="relative overflow-hidden aspect-[4/3]">
            <img
              src={client.imageUrl || '/mainImage.jpg'}
              alt={client.name}
              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
              loading="lazy"
            />
          </div>
          <div className="p-2 text-center font-medium group-hover:text-primary transition-colors">
            {client.name}
          </div>
        </div>
      ))}
    </div>
  );
};

const ProjectSelectorWithImages = ({ clientId, onProjectSelect }) => {
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    if (!clientId) return;

    fetchWithRetry({
      url: `${API_BASE_URL}/api/post`,
      data: {
        function_name: FUNCTION_NAMES.PRISMA,
        operation: 'project.findByClientId',
        payload: { clientId },
      },
      label: 'projects',
    }).then(setProjects);
  }, [clientId]);

  return (
    <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 md:gap-4">
      {projects.map(project => (
        <div
          key={project.id}
          onClick={() => onProjectSelect(project.id)}
          className="cursor-pointer group rounded-xl overflow-hidden border border-border hover:border-primary hover:shadow-md transition-all duration-200"
        >
          <div className="relative overflow-hidden aspect-[4/3]">
            <img
              src={project.imageUrl || '/mainImage.jpg'}
              alt={project.name}
              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
              loading="lazy"
            />
          </div>
          <div className="p-2 text-center font-medium group-hover:text-primary transition-colors">
            {project.name}
          </div>
        </div>
      ))}
    </div>
  );
};

const TourListBySpace = ({ spaceId, projectId, clientId }) => {
  const [tours, setTours] = useState([]);
  const { hasPermission } = useUser();

  const isAdmin = hasPermission('ADMIN_USER');
  const isFullEditor = hasPermission('FULL_EDITOR');
  const canEdit = isAdmin || isFullEditor;

  useEffect(() => {
    if (!clientId || !projectId || !spaceId) {
      setTours([]);
      return;
    }
    
    fetchWithRetry({
      url: `${API_BASE_URL}/api/post`,
      data: {
        function_name: FUNCTION_NAMES.PRISMA,
        operation: 'tour.findBySpaceId',
        payload: { spaceId, projectId, clientId },
      },
      label: 'tours',
    }).then(setTours);
  }, [spaceId, projectId, clientId]);

  if (tours.length === 0) {
    return <div className="text-center text-gray-500">No tours available for this space.</div>;
  }

  return (
    <div className="space-y-3">
      {tours.map(tour => (
        <SubAccordion
          key={tour.id}
          title={tour.name}
          icon={<Eye className="h-4 w-4" />}
          badge={<Badge variant="success" className="text-xs">Active Tour</Badge>}
          defaultOpen={false}
        >
          <PublicTourLinkManager
            tourId={tour.id}
            showToggle={canEdit}
            showEditButton={canEdit}
            showEditorButton={canEdit}
          />
        </SubAccordion>
      ))}
    </div>
  );
};

// Component for space checklists section
const SpaceChecklistsSection = ({ spaceChecklists }) => {
  return (
    <div className="space-y-3">
      {spaceChecklists.map(spaceChecklist => (
        <SpaceChecklistAccordion
          key={spaceChecklist.id}
          spaceChecklist={spaceChecklist}
        />
      ))}
    </div>
  );
};

// Component for space attachments section  
const SpaceAttachmentsSection = ({ spaceAttachments }) => {
  return (
    <div className="space-y-3">
      {spaceAttachments.map(attachment => (
        <S3FileViewer
          key={attachment.id}
          filePath={attachment.priortyUrl}
          s3Path={attachment.priortyUrlS3Path}
          fileName={attachment.priortyUrl.split('/').pop()}
          attachmentType="DOCUMENT"
        />
      ))}
    </div>
  );
};

// Component for individual space checklist with PDF generation
const SpaceChecklistAccordion = ({ spaceChecklist }) => {
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const checklistRef = React.useRef(null);

  const generatePDF = async () => {
    if (!checklistRef.current) return;
    
    setIsGeneratingPDF(true);
    try {
      const element = checklistRef.current;
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        allowTaint: false,
        backgroundColor: '#ffffff'
      });
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });
      
      const imgWidth = 210;
      const pageHeight = 295;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;
      
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      
      // Generate filename with checklist name and date
      const currentDate = new Date().toISOString().split('T')[0];
      const filename = `checklist-${spaceChecklist.checklist.name}-${spaceChecklist.space.name}-${currentDate}.pdf`;
      
      pdf.save(filename);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('שגיאה ביצירת קובץ PDF');
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  return (
    <SubAccordion
      title={`${spaceChecklist.checklist.name} - ${spaceChecklist.space.name}`}
      icon={<FileText className="h-4 w-4 text-blue-600" />}
      badge={
        <div className="flex gap-2">
          <Badge variant={spaceChecklist.status === "הושלם" ? "success" : "secondary"} className="text-xs">
            {spaceChecklist.totalPercentage}%
          </Badge>
          <Badge variant="outline" className="text-xs">
            {spaceChecklist.spacesChecklistItems.length} items
          </Badge>
        </div>
      }
      defaultOpen={false}
    >
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <div className="text-sm text-gray-600">
            <div>Status: {spaceChecklist.status}</div>
            <div>Progress: {spaceChecklist.totalPercentage}%</div>
            <div>Rejects: {spaceChecklist.rejectCount}</div>
          </div>
          <Button 
            onClick={generatePDF} 
            disabled={isGeneratingPDF}
            size="sm"
            className="bg-blue-600 hover:bg-blue-700"
          >
            {isGeneratingPDF ? 'יוצר PDF...' : 'צור PDF'}
          </Button>
        </div>

        <div ref={checklistRef} className="space-y-3 p-4 bg-white border rounded">
          <div className="text-center border-b pb-2 mb-4">
            <h3 className="text-lg font-bold">{spaceChecklist.checklist.name}</h3>
            <p className="text-sm text-gray-600">{spaceChecklist.space.name}</p>
            <p className="text-xs text-gray-500">הושלם: {spaceChecklist.totalPercentage}%</p>
          </div>

          {spaceChecklist.spacesChecklistItems.map(item => (
            <div key={item.id} className="border-l-4 border-blue-200 pl-4 py-3 bg-gray-50 rounded">
              <h4 className="font-medium text-sm mb-2">
                {item.ChecklistItem?.title || 'Unknown Item'}
              </h4>
              
              {item.ChecklistItem?.description && (
                <p className="text-sm text-gray-600 mb-3">{item.ChecklistItem.description}</p>
              )}

              {/* Attachments */}
              {item.attachments && item.attachments.length > 0 && (
                <div className="mb-3">
                  <h5 className="text-sm font-medium mb-2">קבצים מצורפים:</h5>
                  <div className="space-y-2">
                    {item.attachments.map(attachment => (
                      <div key={attachment.id} className="text-xs bg-white p-2 rounded border">
                        <div className="flex items-center gap-2">
                          {attachment.attachmentType === 'IMAGE360' || attachment.attachmentType === 'TABLETIMAGE' ? 
                            <Image className="h-3 w-3" /> : 
                            attachment.attachmentType === 'VIDEO' ? 
                            <Video className="h-3 w-3" /> : 
                            <Paperclip className="h-3 w-3" />
                          }
                          <span className="font-medium">{attachment.filePath.split('/').pop()}</span>
                          <Badge variant={attachment.uploadStatus === 'UPLOADED' ? "success" : "secondary"} className="text-xs">
                            {attachment.uploadStatus}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Push Queue Status */}
              {item.pushQueueItems && item.pushQueueItems.length > 0 && (
                <div>
                  <h5 className="text-sm font-medium mb-2">סטטוס אישורים:</h5>
                  <div className="space-y-1">
                    {item.pushQueueItems.map(pushItem => (
                      <div key={pushItem.id} className="text-xs bg-white p-2 rounded border">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Badge 
                              variant={pushItem.approve ? 'success' : 
                                     pushItem.reject ? 'destructive' : 'secondary'}
                              className="text-xs"
                            >
                              {pushItem.approve ? 'מאושר' : 
                               pushItem.reject ? 'נדחה' : 'ממתין'}
                            </Badge>
                            <span>{pushItem.changeType}</span>
                          </div>
                          <Badge variant="outline" className="text-xs">
                            {pushItem.pushStatus}
                          </Badge>
                        </div>
                        {pushItem.remark && (
                          <div className="mt-1 text-gray-600">הערה: {pushItem.remark}</div>
                        )}
                        {pushItem.rejectRemark && (
                          <div className="mt-1 text-red-600">סיבת דחייה: {pushItem.rejectRemark}</div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </SubAccordion>
  );
};


// Simple Sub-Accordion Component for content within main sections
const SubAccordion = ({ title, icon, children, defaultOpen = false, badge = null }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="border border-gray-200 rounded-lg mb-3">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 transition-colors rounded-t-lg"
      >
        <div className="flex items-center space-x-2">
          {icon}
          <span className="font-medium text-sm">{title}</span>
          {badge}
        </div>
        {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
      </button>
      {isOpen && (
        <div className="p-3 border-t border-gray-200 bg-white rounded-b-lg">
          {children}
        </div>
      )}
    </div>
  );
};



// Settings component for BuildingSVG customization
const BuildingSVGSettings = ({ customSettings, setCustomSettings, saveSettings }) => {
  const handleChange = (field, value) => {
    setCustomSettings(prev => ({
      ...prev,
      [field]: typeof value === 'string' ? Number(value) : value,
    }));
  };

  return (
    <Card className="mb-6">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center justify-between">
          <span>Layout Settings</span>
          <Button size="sm" onClick={saveSettings}>
            <Save className="h-4 w-4 mr-2" />
            Save as Default
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-4">
          <div className="space-y-2">
            <Label htmlFor="spaceWidth">Space Width</Label>
            <Input
              id="spaceWidth"
              type="number"
              value={customSettings.spaceWidth}
              onChange={e => handleChange('spaceWidth', e.target.value)}
              min={50}
              max={500}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="spaceHeight">Space Height</Label>
            <Input
              id="spaceHeight"
              type="number"
              value={customSettings.spaceHeight}
              onChange={e => handleChange('spaceHeight', e.target.value)}
              min={40}
              max={300}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="spaceGap">Space Gap</Label>
            <Input
              id="spaceGap"
              type="number"
              value={customSettings.spaceGap}
              onChange={e => handleChange('spaceGap', e.target.value)}
              min={5}
              max={100}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="fontSize">Font Size</Label>
            <Input
              id="fontSize"
              type="number"
              value={customSettings.fontSize}
              onChange={e => handleChange('fontSize', e.target.value)}
              min={10}
              max={40}
            />
          </div>
        </div>

        <div className="flex items-center space-x-2">
          <Switch
            id="useCustomSettings"
            checked={customSettings.enabled}
            onCheckedChange={checked => handleChange('enabled', checked)}
          />
          <Label htmlFor="useCustomSettings">Use custom settings</Label>
        </div>
      </CardContent>
    </Card>
  );
};

const TourExplorer = () => {
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedSpace, setSelectedSpace] = useState(null);
  const [allTours, setAllTours] = useState([]);
  const [allChecklists, setAllChecklists] = useState([]);
  const [spaceChecklists, setSpaceChecklists] = useState([]);
  const [spaceAttachments, setSpaceAttachments] = useState([]);
  const [showSettings, setShowSettings] = useState(false);
  const [customSettings, setCustomSettings] = useState({
    enabled: false,
    spaceWidth: 220,
    spaceHeight: 120,
    spaceGap: 25,
    fontSize: 24,
  });

  // New state for SpaceModal
  const [showSpaceModal, setShowSpaceModal] = useState(false);
  const [modalSpaceId, setModalSpaceId] = useState(null);
  const [currentProject, setCurrentProject] = useState(null);

  const topRef = useRef(null);
  const spacePanelRef = useRef(null);
  const { hasPermission } = useUser();

  const isAdmin = hasPermission('ADMIN_USER');
  const canDeleteSpace = isAdmin; // Only ADMIN_USER can delete spaces

  // Load saved settings from localStorage
  useEffect(() => {
    const savedSettings = localStorage.getItem('tourExplorer_buildingSvgSettings');
    if (savedSettings) {
      try {
        setCustomSettings(JSON.parse(savedSettings));
      } catch (e) {
        console.error('Error parsing saved settings', e);
      }
    }

    // Load previously selected client/project from localStorage
    const savedLocation = localStorage.getItem('tourExplorer_location');
    if (savedLocation) {
      try {
        const { clientId, projectId } = JSON.parse(savedLocation);
        if (clientId) setSelectedClient(clientId);
        if (projectId) setSelectedProject(projectId);
      } catch (e) {
        console.error('Error parsing saved location', e);
      }
    }
  }, []);

  // Save client/project selection to localStorage
  useEffect(() => {
    if (selectedClient || selectedProject) {
      localStorage.setItem(
        'tourExplorer_location',
        JSON.stringify({
          clientId: selectedClient,
          projectId: selectedProject,
        })
      );
    }
  }, [selectedClient, selectedProject]);

  // Fetch project details when selectedProject changes
  useEffect(() => {
    if (selectedProject) {
      fetchWithRetry({
        url: `${API_BASE_URL}/api/post`,
        data: {
          function_name: FUNCTION_NAMES.PRISMA,
          operation: 'project.findUnique',
          payload: selectedProject,
        },
        label: 'project details',
      }).then(setCurrentProject);
    } else {
      setCurrentProject(null);
    }
  }, [selectedProject]);

  const scrollToTop = () => {
    if (topRef.current) {
      topRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const saveSettings = () => {
    localStorage.setItem('tourExplorer_buildingSvgSettings', JSON.stringify(customSettings));
    setShowSettings(false);
  };

  useEffect(() => {
    if (!selectedClient || !selectedProject) return;
    try {
      // Fetch both tours and checklists for the project
      Promise.all([
        fetchWithRetry({
          url: `${API_BASE_URL}/api/post`,
          data: {
            function_name: FUNCTION_NAMES.PRISMA,
            operation: 'tour.findByProjectId',
            payload: { projectId: selectedProject },
          },
          label: 'all_tours',
        }),
        fetchWithRetry({
          url: `${API_BASE_URL}/api/post`,
          data: {
            function_name: FUNCTION_NAMES.PRISMA,
            operation: 'spacesChecklist.findMany',
            payload: {
              where: {
                space: {
                  projectId: selectedProject
                }
              },
              include: {
                checklist: true,
                space: true
              }
            },
          },
          label: 'all_checklists',
        })
      ]).then(([toursData, checklistsData]) => {
        setAllTours(toursData);
        setAllChecklists(checklistsData);
      });
    } catch (e) {
      console.error('Error fetching tours and checklists', e);
    }
  }, [selectedClient, selectedProject]);

  const tourMap = useMemo(() => {
    const map = {};
    allTours.forEach(tour => {
      map[tour.spaceId] = true;
    });
    return map;
  }, [allTours]);

  const checklistMap = useMemo(() => {
    const map = {};
    allChecklists.forEach(checklist => {
      map[checklist.spaceId] = true;
    });
    return map;
  }, [allChecklists]);

  // Fetch space-specific data when selectedSpace changes
  useEffect(() => {
    if (!selectedSpace) {
      setSpaceChecklists([]);
      setSpaceAttachments([]);
      return;
    }

    // Clear previous data immediately
    setSpaceChecklists([]);
    setSpaceAttachments([]);

    // Fetch space checklists and attachments in parallel
    Promise.all([
      fetchWithRetry({
        url: `${API_BASE_URL}/api/post`,
        data: {
          function_name: FUNCTION_NAMES.PRISMA,
          operation: 'spacesChecklist.findMany',
          payload: {
            where: { spaceId: parseInt(selectedSpace) },
            include: {
              checklist: true,
              space: true,
              spacesChecklistItems: {
                include: {
                  ChecklistItem: true,
                  attachments: true,
                  pushQueueItems: true
                }
              }
            }
          },
        },
        label: 'space checklists',
      }),
      fetchWithRetry({
        url: `${API_BASE_URL}/api/post`,
        data: {
          function_name: FUNCTION_NAMES.PRISMA,
          operation: 'spaceAttachments.findMany',
          payload: {
            where: { spaceId: parseInt(selectedSpace) }
          },
        },
        label: 'space attachments',
      })
    ]).then(([checklistsData, attachmentsData]) => {
      setSpaceChecklists(checklistsData || []);
      setSpaceAttachments(attachmentsData || []);
    }).catch((error) => {
      console.error('Error fetching space data:', error);
      setSpaceChecklists([]);
      setSpaceAttachments([]);
    });
  }, [selectedSpace]);

  const resetFlow = () => {
    localStorage.removeItem('tourExplorer_location');
    setSelectedClient(null);
    setSelectedProject(null);
    setSelectedSpace(null);
    setSpaceChecklists([]);
    setSpaceAttachments([]);
  };

  const handleBackFromSpace = () => {
    setSelectedSpace(null);
    setSpaceChecklists([]);
    setSpaceAttachments([]);
    scrollToTop();
  };

  return (
    <div ref={topRef} className="p-4 space-y-4">
      <div className="flex justify-between items-center mb-6">
        <Button variant="outline" size="sm" onClick={resetFlow} className="flex items-center gap-2">
          <RotateCcw className="h-4 w-4" />
          Start Over
        </Button>

        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowSettings(!showSettings)}
          className="ml-auto"
        >
          <Settings className="h-4 w-4 mr-2" />
          Layout Settings
        </Button>
      </div>

      {showSettings && (
        <BuildingSVGSettings
          customSettings={customSettings}
          setCustomSettings={setCustomSettings}
          saveSettings={saveSettings}
        />
      )}

      {!selectedClient && <ClientSelectorWithImages onClientSelect={setSelectedClient} />}
      {selectedClient && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            setSelectedClient(null);
            setSelectedProject(null);

            return;
          }}
          className="flex items-center mb-4 text-primary hover:text-primary/80"
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          Back to Clients
        </Button>
      )}

      {selectedClient && !selectedProject && (
        <ProjectSelectorWithImages clientId={selectedClient} onProjectSelect={setSelectedProject} />
      )}
      {selectedProject && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            setSelectedProject(null);

            return;
          }}
          className="flex items-center mb-4 text-primary hover:text-primary/80"
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          Back to Projects
        </Button>
      )}

      {/* Always show the building/lot selector, but pass null values if not selected */}
      {selectedClient && selectedProject && (
        <div>
          <AccordionPanel
            title="סיורים עבור הבניין"
            onBackClick={() => setSelectedSpace(null)}
            defaultOpen={!!selectedClient && !!selectedProject}
          >
            <LotBuildingSelectorWithSVG
              clientId={selectedClient}
              projectId={selectedProject}
              tourMap={tourMap}
              checklistMap={checklistMap}
              customSettings={customSettings.enabled ? customSettings : null}
              onSpaceSelect={spaceId => {
                // Open modal for space details (new behavior)
                setModalSpaceId(spaceId);
                setShowSpaceModal(true);
                // Keep old behavior for admin users who prefer the accordion view
                if (isAdmin && false) { // Currently disabled, can enable later if needed
                  setSelectedSpace(spaceId);
                  setTimeout(() => {
                    spacePanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }, 200);
                }
              }}
              canDeleteSpace={canDeleteSpace}
              disabled={!selectedClient || !selectedProject}
            />
            {(!selectedClient || !selectedProject) && (
              <div className="flex flex-col items-center justify-center p-8 text-center text-muted-foreground">
                <Home className="h-12 w-12 mb-3 opacity-50" />
                <p className="text-lg font-medium">Please select a client and project first</p>
                <p className="text-sm">The building explorer will be available after selection</p>
              </div>
            )}
          </AccordionPanel>
        </div>
      )}

      {selectedSpace && (
        <div ref={spacePanelRef} className="space-y-4">
          {/* Only show attachments accordion if there are attachments */}
          {spaceAttachments.length > 0 && (
            <AccordionPanel
              title="מסמכים לחלל"
              onBackClick={handleBackFromSpace}
              defaultOpen
            >
              <SpaceAttachmentsSection spaceAttachments={spaceAttachments} />
            </AccordionPanel>
          )}

          <AccordionPanel
            title="סיורים עבור החלל"
            defaultOpen={spaceAttachments.length === 0}
            onBackClick={spaceAttachments.length === 0 ? handleBackFromSpace : undefined}
          >
            <TourListBySpace
              clientId={selectedClient}
              projectId={selectedProject}
              spaceId={selectedSpace}
            />
          </AccordionPanel>

          {/* Only show checklists accordion if there are checklists */}
          {spaceChecklists.length > 0 && (
            <AccordionPanel
              title="צ׳קליסטים"
              defaultOpen
            >
              <SpaceChecklistsSection spaceChecklists={spaceChecklists} />
            </AccordionPanel>
          )}
        </div>
      )}

      {/* Space Modal */}
      <SpaceModal
        isOpen={showSpaceModal}
        onClose={() => setShowSpaceModal(false)}
        spaceId={modalSpaceId}
        projectId={selectedProject}
        clientId={selectedClient}
        project={currentProject}
      />

    </div>
  );
};

export default TourExplorer;
